/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Flex, Text } from "@chakra-ui/react";
import { useSelector } from "react-redux";
import { cardsSelector, userSelector } from "store/user/selector";
import { CardsPanel, OrderInfoCard, ShippingDetailsForm } from "./components";
import { useShippingValidity, useStripePaymentForm } from "./hooks";
import type { PaymentStepProps } from "./types";

export default function PaymentStep(props: PaymentStepProps) {
  const {
    selectedOrder,
    onPickPaymentInfo,
    showShippingDetails,
    shippingRecipient,
    shippingStreet,
    shippingCity,
    shippingZip,
    shippingState,
    shippingCountry,
    onChangeShippingRecipient,
    onChangeShippingStreet,
    onChangeShippingCity,
    onChangeShippingZip,
    onChangeShippingState,
    onChangeShippingCountry,
    onShippingValidityChange,
    perPageUsd,
    pageCount,
    effectivePrice,
  } = props;

  const user = useSelector(userSelector);
  const cards = useSelector(cardsSelector);

  useShippingValidity({
    showShippingDetails,
    shippingRecipient,
    shippingStreet,
    shippingCity,
    shippingZip,
    shippingState,
    shippingCountry,
    onShippingValidityChange,
  });

  const {
    openCardInputForm,
    selectedPaymentInfo,
    cardBrand,
    cardHolderName,
    setCardHolderName,
    onSelectSavedCard,
    onRemoveCard,
    onToggleAddNewCard,
  } = useStripePaymentForm({
    cardsCount: cards?.length || 0,
    userId: user?.user_id,
    onPickPaymentInfo,
  });

  return (
    <Box bg="#F5F7FA" p="24px" borderRadius="12px" border="1px solid #E5E7EB">
      <Flex direction={{ base: "column", md: "row" }} gap={{ base: "16px", md: "24px" }}>
        <Box w={{ base: "100%", md: "40%" }}>
          <OrderInfoCard selectedOrder={selectedOrder} />

          {effectivePrice != null && (
            <Box
              mt="16px"
              bg="#FFFFFF"
              border="1px solid #E5E7EB"
              borderRadius="12px"
              overflow="hidden"
            >
              <Box px="16px" py="14px" borderBottom="1px solid #E5E7EB">
                <Text fontSize="16px" fontWeight="600" color="#0B0E10">
                  Order summary
                </Text>
              </Box>

              {perPageUsd ? (
                <>
                  <Flex justify="space-between" align="center" px="16px" py="12px" borderBottom="1px solid #E5E7EB">
                    <Text fontSize="14px" color="#111827">Pages</Text>
                    <Text fontSize="14px" fontWeight="600" color="#111827">
                      {pageCount ?? 0}
                    </Text>
                  </Flex>
                  <Flex justify="space-between" align="center" px="16px" py="12px" borderBottom="1px solid #E5E7EB">
                    <Text fontSize="14px" color="#111827">Rate</Text>
                    <Text fontSize="14px" fontWeight="600" color="#111827">
                      ${perPageUsd.toFixed(2)} / page
                    </Text>
                  </Flex>
                </>
              ) : null}

              <Flex justify="space-between" align="center" px="16px" py="12px">
                <Text fontSize="14px" fontWeight="600" color="#111827">Total</Text>
                <Text fontSize="14px" fontWeight="700" color="#2563EB">
                  ${effectivePrice.toFixed(2)}
                </Text>
              </Flex>
            </Box>
          )}
        </Box>

        <Box w={{ base: "100%", md: "60%" }}>
          {showShippingDetails ? (
            <ShippingDetailsForm
              shippingRecipient={shippingRecipient}
              shippingStreet={shippingStreet}
              shippingCity={shippingCity}
              shippingZip={shippingZip}
              shippingState={shippingState}
              shippingCountry={shippingCountry}
              onChangeShippingRecipient={onChangeShippingRecipient}
              onChangeShippingStreet={onChangeShippingStreet}
              onChangeShippingCity={onChangeShippingCity}
              onChangeShippingZip={onChangeShippingZip}
              onChangeShippingState={onChangeShippingState}
              onChangeShippingCountry={onChangeShippingCountry}
            />
          ) : null}

          <CardsPanel
            cards={cards}
            selectedPaymentInfo={selectedPaymentInfo}
            openCardInputForm={openCardInputForm}
            cardBrand={cardBrand}
            cardHolderName={cardHolderName}
            setCardHolderName={setCardHolderName}
            onSelectSavedCard={onSelectSavedCard}
            onRemoveCard={onRemoveCard}
            onToggleAddNewCard={onToggleAddNewCard}
          />
        </Box>
      </Flex>
    </Box>
  );
}
